import type {
    AccountNo,
    AdditionalData,
    FLAG,
    Account as LegacyAccount,
} from '@shared/products-types';
import { isFeatureEnabled } from '@luna/gatekeeper';

import type { Account } from './types/aggregationService';
import { getAccountIcon } from '../services/iconService';

function mapAdditionalData(acc: Account): AdditionalData {
    const embedded = acc._embedded;
    return {
        isFavorite: embedded.customization.favorite || false,
        color: embedded.customization.color?.id || '',
        hidden: embedded.customization.hiddenOnOverview || false,
        position: 0,
        upcomingOrderDays: 0,
        accountColor: embedded.customization.color
            ? {
                  name: embedded.customization.color.name,
                  hexCodeLight: [embedded.customization.color.hexCodeLight],
                  hexCodeDark: embedded.customization.color.hexCodeDark,
                  accessibilityLabel: embedded.customization.color.accessibilityLabel,
                  label: embedded.customization.color.accessibilityLabel,
              }
            : undefined,
    };
}

function mapAccountNo(acc: Account): AccountNo {
    return {
        prefix: acc.bankConnection?.prefix || undefined,
        number: acc.bankConnection?.accountNumber || undefined,
        bankCode: acc.bankConnection?.bankCode || undefined,
        bic: acc.bankConnection?.bic || '',
        countryCode: acc.bankConnection?.countryCode || undefined,
        displayAccountNumber: acc.bankConnection?.accountNumber || undefined,
        iban: acc.bankConnection?.iban || '',
    };
}

export function mapAdr10AccountToAccount(acc: Account): LegacyAccount {
    const embedded = acc._embedded;

    const mapUsingLegacyAccount = isFeatureEnabled((f) => f.adr10LegacyAccountMapping);

    if (mapUsingLegacyAccount) {
        return {
            permissionFlags: acc.permissionFlags,
            behaviourFlags: acc.behaviourFlags,
            ...embedded.legacyAccount,
            currency: acc.currency ?? undefined,
            alias: embedded.customization.name || '',
            additionalData: mapAdditionalData(acc),
            icon: getAccountIcon({
                ...acc,
                type: acc._embedded.legacyAccount?.type,
            }),
            origin: acc.origin,
        };
    }

    return {
        id: acc.id,
        type: acc.type as LegacyAccount['type'],
        subType: acc.subType || undefined,
        productI18N: acc.localizedName,
        description: acc.description,
        product: acc.fullName,
        creationDate: acc.creationDate || undefined,
        // balance
        balance: acc.balance?.current?.amount,
        dateOfBalance: acc.balance?.current?.dateOfBalance || undefined,
        // disposable balance
        disposable: acc.balance?.disposable?.amount || undefined,
        disposableLocalCurrency: acc.balance?.disposable?.localAmount || undefined,
        dateOfDisposableBalance: acc.balance?.disposable?.dateOfBalance || undefined,
        // bank connection data
        accountno: mapAccountNo(acc),
        // others
        currency: acc.currency ?? undefined,
        debitInterestRate: Number.isFinite(acc.debitInterestRate)
            ? Number(acc.debitInterestRate)
            : undefined,
        flags: acc.flags as FLAG[],
        behaviourFlags: acc.behaviourFlags,
        alias: embedded.customization.name || '',
        color: embedded.customization.color?.name || '',
        additionalData: mapAdditionalData(acc),
        // direct debit
        directDebit: acc.directDebit ?? undefined,
        permissionFlags: acc.permissionFlags,
        icon: getAccountIcon(acc),
        origin: acc.origin,
        limitOverview: acc.limitOverview as LegacyAccount['limitOverview'],
    };
}
